'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

type PortalType = 'employee' | 'distributor' | 'retailer';

interface LoginForm {
  mobile: string;
  password: string;
}

const portalRoleMap: Record<PortalType, string[]> = {
  employee: ['admin', 'employee'],
  distributor: ['distributor'],
  retailer: ['retailer'],
};

const portalConfig: Record<PortalType, { label: string; icon: string; description: string; gradient: string; activeBg: string; activeBorder: string; activeText: string }> = {
  employee: {
    label: 'Employee / Admin',
    icon: 'UserGroupIcon',
    description: 'Manage territory, onboard distributors & retailers',
    gradient: 'from-blue-600 to-indigo-600',
    activeBg: 'bg-blue-600',
    activeBorder: 'border-blue-600',
    activeText: 'text-white',
  },
  distributor: {
    label: 'Distributor',
    icon: 'BuildingOfficeIcon',
    description: 'Place orders, manage retailers, download price lists',
    gradient: 'from-violet-600 to-purple-600',
    activeBg: 'bg-violet-600',
    activeBorder: 'border-violet-600',
    activeText: 'text-white',
  },
  retailer: {
    label: 'Retailer',
    icon: 'BuildingStorefrontIcon',
    description: 'Place orders, view invoices, download price list',
    gradient: 'from-emerald-600 to-teal-600',
    activeBg: 'bg-emerald-600',
    activeBorder: 'border-emerald-600',
    activeText: 'text-white',
  },
};

const gradientMap: Record<PortalType, string> = {
  employee: 'from-blue-600/20 via-indigo-600/10 to-transparent',
  distributor: 'from-violet-600/20 via-purple-600/10 to-transparent',
  retailer: 'from-emerald-600/20 via-teal-600/10 to-transparent',
};

interface BlockedInfo {
  shopName: string;
  entityType: 'distributor' | 'retailer';
}

export default function LoginScreen() {
  const router = useRouter();
  const [selectedPortal, setSelectedPortal] = useState<PortalType>('employee');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blockedInfo, setBlockedInfo] = useState<BlockedInfo | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    setBlockedInfo(null);
    try {
      const supabase = createClient();
      const email = `${data.mobile}@suryanindia.app`;

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password: data.password,
      });

      if (authError) {
        toast.error('Invalid credentials — check mobile number and password');
        setLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', authData.user.id)
        .maybeSingle();

      if (profileError || !profile) {
        toast.error('Account not found. Please contact your administrator.');
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      if (profile.status === 'inactive') {
        await supabase.auth.signOut();
        setLoading(false);

        // Fetch shop/company name for blocked message
        let shopName = profile.name || 'Your Account';
        if (profile.role === 'distributor') {
          const { data: dist } = await supabase
            .from('distributors')
            .select('company_name')
            .eq('user_profile_id', profile.id)
            .maybeSingle();
          if (dist?.company_name) shopName = dist.company_name;
          setBlockedInfo({ shopName, entityType: 'distributor' });
        } else if (profile.role === 'retailer') {
          const { data: ret } = await supabase
            .from('retailers')
            .select('shop_name')
            .eq('user_profile_id', profile.id)
            .maybeSingle();
          if (ret?.shop_name) shopName = ret.shop_name;
          setBlockedInfo({ shopName, entityType: 'retailer' });
        } else {
          toast.error('Your account has been blocked. Contact your administrator.');
        }
        return;
      }

      const allowedRoles = portalRoleMap[selectedPortal];
      if (!allowedRoles.includes(profile.role)) {
        toast.error(`This account does not have access to the ${portalConfig[selectedPortal].label} portal.`);
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      if (typeof window !== 'undefined') {
        localStorage.setItem('suryan_user', JSON.stringify({
          id: profile.id,
          role: profile.role,
          name: profile.name,
          mobile: profile.mobile,
          isAdmin: profile.is_admin || false,
        }));
      }

      toast.success(`Welcome back, ${profile.name}!`);
      const redirect = profile.redirect || (profile.role === 'distributor' ? '/distributor-dashboard' : profile.role === 'retailer' ? '/retailer-dashboard' : '/admin-employee-dashboard');
      setTimeout(() => router.push(redirect), 500);
    } catch (err: any) {
      toast.error(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cfg = portalConfig[selectedPortal];

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex overflow-hidden">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[52%] flex-col justify-between p-14 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-[#0d1530] to-[#0a0f1e]" />
          <div className={`absolute inset-0 bg-gradient-to-br ${gradientMap[selectedPortal]} transition-all duration-700`} />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-blue-600/10 blur-[100px] pointer-events-none" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-indigo-500/10 blur-[80px] pointer-events-none" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="flex items-center justify-center">
              <AppLogo size={144} />
            </div>
            <div>
              <span className="text-white font-bold text-xl block leading-tight tracking-tight">Suryan India</span>
              <span className="text-slate-500 text-xs block tracking-widest uppercase">by Growwmore</span>
            </div>
          </div>

          <div className="mb-12">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-slate-400 text-xs font-medium tracking-wide">Distribution Management Platform</span>
            </div>
            <h1 className="text-5xl font-bold text-white leading-[1.1] tracking-tight mb-5">
              Powering India&apos;s<br />
              <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Distribution</span><br />
              Network
            </h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-sm">
              Connect manufacturers with distributors and retailers across India through intelligent geo-based territory management.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-sm">
            {[
              { label: '2,400+', sub: 'Active Distributors', icon: '🏢' },
              { label: '18,000+', sub: 'Retailers Onboarded', icon: '🏪' },
              { label: 'AP & TG', sub: 'Full Coverage', icon: '📍' },
              { label: '48 SKUs', sub: 'In Catalog', icon: '📦' },
            ].map((stat) => (
              <div key={stat.sub} className="bg-white/[0.04] backdrop-blur-sm rounded-2xl p-4 border border-white/[0.08] hover:bg-white/[0.07] transition-colors">
                <div className="text-xl mb-1">{stat.icon}</div>
                <p className="text-white font-bold text-xl font-tabular">{stat.label}</p>
                <p className="text-slate-500 text-xs mt-0.5">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-slate-600 text-xs">© 2026 Growwmore Technologies Pvt. Ltd. All rights reserved.</p>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <div className="flex items-center justify-center">
              <AppLogo size={144} />
            </div>
            <div>
              <span className="text-slate-900 font-bold text-lg block">Suryan India</span>
              <span className="text-slate-400 text-xs block">by Growwmore</span>
            </div>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">Welcome back</h2>
            <p className="text-slate-500 text-sm">Sign in to your portal to continue</p>
          </div>

          {/* Blocked Account Error Banner — persistent until cleared */}
          {blockedInfo && (
            <div className="mb-5 rounded-xl border-2 border-red-300 bg-red-50 p-4 relative">
              <button
                onClick={() => setBlockedInfo(null)}
                className="absolute top-3 right-3 text-red-400 hover:text-red-600 transition-colors"
                title="Clear this message"
              >
                <Icon name="XMarkIcon" size={18} variant="outline" />
              </button>
              <div className="flex items-start gap-3 pr-6">
                <div className="w-9 h-9 rounded-full bg-red-100 border border-red-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="NoSymbolIcon" size={18} variant="outline" className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-red-800 mb-1">
                    Account Blocked — {blockedInfo.shopName}
                  </p>
                  <p className="text-xs font-semibold text-red-700 leading-relaxed">
                    Your {blockedInfo.entityType === 'distributor' ? 'distributor' : 'retailer'} account is currently in a <span className="font-bold">blocked state</span>.
                  </p>
                  <p className="text-xs font-medium text-red-600 mt-1.5 leading-relaxed">
                    Please complete your distributor deposit to activate your account. If you have already paid, please contact the company for assistance.
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded-full px-2.5 py-1">
                      <Icon name="CurrencyRupeeIcon" size={12} variant="outline" />
                      Complete Deposit
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded-full px-2.5 py-1">
                      <Icon name="PhoneIcon" size={12} variant="outline" />
                      Contact Company
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Portal Selector */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Select Portal</p>
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(portalConfig) as [PortalType, typeof portalConfig[PortalType]][]).map(([key, config]) => (
                <button
                  key={`portal-${key}`}
                  onClick={() => { setSelectedPortal(key); setBlockedInfo(null); }}
                  className={`relative flex flex-col items-center p-3.5 rounded-xl border-2 transition-all duration-200 text-center overflow-hidden ${
                    selectedPortal === key
                      ? `${config.activeBorder} ${config.activeBg} shadow-lg`
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300'
                  }`}
                >
                  <Icon
                    name={config.icon as Parameters<typeof Icon>[0]['name']}
                    size={20}
                    variant="outline"
                    className={selectedPortal === key ? 'text-white' : 'text-slate-500'}
                  />
                  <span className={`text-xs font-bold mt-1.5 leading-tight ${selectedPortal === key ? 'text-white' : 'text-slate-600'}`}>
                    {key === 'employee' ? 'Employee' : key.charAt(0).toUpperCase() + key.slice(1)}
                  </span>
                </button>
              ))}
            </div>
            <div className={`mt-2.5 rounded-lg px-3 py-2 bg-gradient-to-r ${cfg.gradient} bg-opacity-10`} style={{ background: 'rgba(0,0,0,0.03)' }}>
              <p className="text-xs text-slate-500 font-medium">{cfg.description}</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="mobile" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Mobile Number
              </label>
              <div className="relative flex items-center">
                <div className="absolute left-0 flex items-center h-full pl-3.5 pr-3 border-r border-slate-200 pointer-events-none">
                  <span className="text-slate-500 text-sm font-semibold">+91</span>
                </div>
                <input
                  id="mobile"
                  type="tel"
                  maxLength={10}
                  placeholder="9876543210"
                  {...register('mobile', {
                    required: 'Mobile number is required',
                    pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number' },
                  })}
                  className="w-full pl-16 pr-4 py-3 border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50 text-slate-900 placeholder:text-slate-400 transition-all"
                />
              </div>
              {errors.mobile && <p className="text-xs font-semibold text-red-500 mt-1.5">{errors.mobile.message}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-semibold text-slate-700">
                  Password
                </label>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  {...register('password', { required: 'Password is required' })}
                  className="w-full px-4 pr-12 py-3 border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50 text-slate-900 placeholder:text-slate-400 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} variant="outline" />
                </button>
              </div>
              {errors.password && <p className="text-xs font-semibold text-red-500 mt-1.5">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 bg-gradient-to-r ${cfg.gradient} text-white font-bold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg mt-2`}
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <Icon name="ArrowRightIcon" size={16} variant="outline" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-6">
            Suryan India Distribution Platform · Powered by Growwmore
          </p>
        </div>
      </div>
    </div>
  );
}